export interface PermissionCategory {
	id: string
	name: string
	created_at: string
	updated_at: string
	deleted_at: string | null
}

export interface Permission {
	id: string
	name: string
	description: string
	permission_category_id: string
	created_at: string
	updated_at: string
	deleted_at: string | null
	permission_category: PermissionCategory
}

// ============================================
// PERMISSION CONSTANTS - Main Focus
// ============================================

export const PERMISSIONS = {
	WALLET: {
		FUND: 'Fund Business Wallet',
		VIEW_BALANCE: 'View Business Wallet Balance',
		WITHDRAW: 'Withdraw from Wallet',
		ADD_CARD: 'Add Card'
	} as const,

	CARD: {
		EDIT: 'Edit Card',
		REMOVE: 'Remove Card',
		ADD: 'Add Card'
	} as const,

	PRODUCT: {
		VIEW: 'View Products'
	} as const,

	PLAN: {
		PURCHASE: 'Purchase Business Plan',
		ADD_STAFF: 'Add Staff to Plan',
		ADD_BENEFICIARIES: 'Add Beneficiaries to Plan',
		ACTIVATE: 'Activate Plan',
		DEACTIVATE: 'Deactivate Business Plan',
		UPGRADE: 'Upgrade Business Plan',
		DOWNGRADE: 'Downgrade Business Plan',
		REMOVE: 'Remove Plan',
		EDIT: 'Edit Plan',
		VIEW_DETAILS: 'View Plan Details'
	} as const,

	ROLE: {
		VIEW: 'View Role',
		VIEW_LIST: 'View Roles List',
		EDIT: 'Edit Role',
		CREATE: 'Create Role',
		DELETE: 'Delete Role'
	} as const,

	TEAM: {
		ADD_MEMBER: 'Add Team Member',
		VIEW_MEMBERS: 'View Team Members',
		REMOVE_MEMBER: 'Remove Team Members',
		DEACTIVATE_MEMBER: 'Deactivate Team Member'
	} as const,

	RENEWAL: {
		DISABLE_AUTO: 'Disable Auto Renewals',
		RENEW_PLAN: 'Renew Plan',
		ENABLE_AUTO: 'Enable Auto Renewal'
	} as const,

	TRANSACTION: {
		VIEW_DETAILS: 'View Transaction Details',
		DOWNLOAD_DETAILS: 'Download Transaction Details'
	} as const,

	CLAIM: {
		MAKE: 'Make Claim',
		VIEW_DETAILS: 'View Claim Details',
		VIEW_LIST: 'View List of Claims',
		DOWNLOAD_REPORT: 'Download Claims Report'
	} as const
} as const

// Get all permission values as a union type
export type PermissionValue = {
	[K in keyof typeof PERMISSIONS]: (typeof PERMISSIONS)[K][keyof (typeof PERMISSIONS)[K]]
}[keyof typeof PERMISSIONS]

// Get a specific category's permissions
export type WalletPermissions = (typeof PERMISSIONS.WALLET)[keyof typeof PERMISSIONS.WALLET]
export type PlanPermissions = (typeof PERMISSIONS.PLAN)[keyof typeof PERMISSIONS.PLAN]
export type TeamPermissions = (typeof PERMISSIONS.TEAM)[keyof typeof PERMISSIONS.TEAM]

export const PERMISSIONS_FLAT = Object.values(PERMISSIONS).flatMap(category => Object.values(category))

// ============================================
// PERMISSION GROUPS (for role-based checks)
// ============================================

export const PERMISSION_GROUPS = {
	FULL_WALLET_ACCESS: [
		PERMISSIONS.WALLET.FUND,
		PERMISSIONS.WALLET.VIEW_BALANCE,
		PERMISSIONS.WALLET.WITHDRAW,
		PERMISSIONS.CARD.EDIT,
		PERMISSIONS.CARD.REMOVE
	] as const,

	FULL_PLAN_MANAGEMENT: [
		PERMISSIONS.PLAN.PURCHASE,
		PERMISSIONS.PLAN.EDIT,
		PERMISSIONS.PLAN.ACTIVATE,
		PERMISSIONS.PLAN.DEACTIVATE,
		PERMISSIONS.PLAN.ADD_STAFF,
		PERMISSIONS.PLAN.ADD_BENEFICIARIES
	] as const,

	CLAIMS_MANAGER: [PERMISSIONS.CLAIM.MAKE, PERMISSIONS.CLAIM.VIEW_LIST, PERMISSIONS.CLAIM.DOWNLOAD_REPORT] as const,

	TEAM_ADMIN: [
		PERMISSIONS.TEAM.ADD_MEMBER,
		PERMISSIONS.TEAM.VIEW_MEMBERS,
		PERMISSIONS.TEAM.REMOVE_MEMBER,
		PERMISSIONS.TEAM.DEACTIVATE_MEMBER
	] as const
} as const
